import React, { useState } from 'react'
import { AlertCircle, CheckCircle, Heart, Users, Clock, ClipboardList, Target } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import DownloadForm from '@/components/common/DownloadForm'

const CaseStudyLincolnshire: React.FC = () => {
  const navigationStore = useNavigationStore()
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)
  const handleBookDemo = () => navigationStore.openDemoModal()

  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How Lincolnshire NHS CCG Met the 7‑Day Referral KPI with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">ReferralCX powers the Elective Activity Coordination Hub (EACH), centralizing referrals across GP, community, and hospital pathways.</p>
              <div className="mt-8 flex gap-3">
                <button onClick={() => setDownloadFormOpen(true)} className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition">Download Full Case Study</button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">referral</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">Centralized intake, triage, auto‑routing, and communications (email/SMS) for EACH.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Target className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">7‑day KPI</div>
              <div className="text-slate-600">Referral processing target met</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <ClipboardList className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">EACH hub</div>
              <div className="text-slate-600">Central service for county</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Thousands / month</div>
              <div className="text-slate-600">Cross‑pathway referrals</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Clock className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Go live: Aug 2021</div>
              <div className="text-slate-600">Rapid onboarding timeline</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">Before ReferralCX, referral intake and triage were dispersed across teams with manual steps and limited status visibility—jeopardizing the 7‑day processing KPI.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Manual, multi‑channel intake with duplicate data entry</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Limited real‑time status for clinicians and coordinators</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Need to demonstrate 7‑day KPI compliance across pathways</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Disparate intake</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Duplicate entry</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">No real‑time status</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">KPI risk</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">Lincolnshire deployed <strong>ReferralCX</strong> to unify intake, triage, routing, and communications in EACH—with time tracking, queues, and dashboards for performance monitoring.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Centralized intake</div><div className="mt-1 text-slate-600">GP, community, nurse practitioner, and hospital referrals in one place.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Automated communications</div><div className="mt-1 text-slate-600">Text/email updates for status and next steps.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Smart triage & routing</div><div className="mt-1 text-slate-600">Queues and rules reduce delays and handoffs.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Performance dashboards</div><div className="mt-1 text-slate-600">Real‑time KPI monitoring, including 7‑day processing.</div></div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>7‑day KPI achieved</strong> with centralized intake, triage, and time tracking.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Higher throughput</strong> handling thousands of referrals monthly across pathways.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Manager visibility</strong> with real‑time dashboards and audit trails for improvement.</span></div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-semibold">Human Impact</h2>
            <p className="mt-4 text-slate-700">Patients and clinicians get faster answers and fewer administrative loops, while coordinators work from a single source of truth.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Quicker progression from referral to appointment</span></li>
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Fewer duplicate calls and emails</span></li>
            </ul>
          </div>
          <aside className="rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-6">
            <blockquote className="text-lg leading-relaxed">“ReferralCX gave us end‑to‑end visibility and helped us consistently meet the 7‑day processing target.”</blockquote>
            <div className="mt-3 text-slate-600">— EACH Leadership, Lincolnshire</div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">Launched in August 2021, ReferralCX established a scalable, KPI‑driven referral backbone for Lincolnshire’s county‑wide coordination hub.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Speed to value</div><div className="text-slate-600 mt-1">Rapid onboarding under fixed timelines</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Operational discipline</div><div className="text-slate-600 mt-1">KPI tracking embedded in daily workflows</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Scales with demand</div><div className="text-slate-600 mt-1">Handles thousands of referrals monthly</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Ready to hit aggressive referral KPIs?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX can centralize intake and keep pathways moving.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="NHS Lincolnshire Case Study"
        pdfPath="/PDF/Detailed Case Studies/Eceptionist_Case+Study_NHS+Lincolnshire.pdf"
      />
    </main>
  )
}

export default CaseStudyLincolnshire


